/*
 * Kopfbereich: Megamenue (Disclosure-Muster), Schublade (natives dialog) und die
 * gemessene Kopfhoehe --header-height fuer klebende Elemente anderer Sektionen.
 */

/*
 * Safari ab iOS 26 faerbt die Flaeche um seine schwebende Leiste in der Farbe des
 * Inhalts an der Bildschirmunterkante — und rechnet sie nicht neu, wenn ein
 * Vollbild-Dialog schliesst. Das Weiss der Schublade blieb dann als Balken stehen
 * (Atakan, iPhone 2026-09-20). Ein Sprung um ein Pixel und zurueck stoesst die
 * Neuberechnung an, ohne dass man es sieht. Nur auf Tippgeraeten.
 */
export function leisteNeuFaerben() {
  if (!window.matchMedia('(pointer: coarse)').matches) return;
  const stand = window.scrollY;
  const ziel = stand > 0 ? stand - 1 : 1;
  window.scrollTo({ top: ziel, behavior: 'instant' });
  requestAnimationFrame(() => window.scrollTo({ top: stand, behavior: 'instant' }));
}

document.addEventListener('dialog:close', () => requestAnimationFrame(leisteNeuFaerben));

/* Megamenue ab 990 px. Zeiger, Klick und Tastatur setzen alle aria-expanded, damit
   sichtbarer und vorgelesener Zustand nie auseinanderlaufen. */
class EratecNavMega extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#beiKlick);
    this.addEventListener('keydown', this.#beiTaste);
    this.addEventListener('focusout', this.#beiFokusAus);
    for (const gruppe of this.querySelectorAll('[data-er-gruppe]')) {
      gruppe.addEventListener('pointerenter', this.#beiZeigerRein);
      gruppe.addEventListener('pointerleave', this.#beiZeigerRaus);
    }
    document.addEventListener('pointerdown', this.#beiKlickAussen);
    document.addEventListener('keydown', this.#beiEscape);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#beiKlickAussen);
    document.removeEventListener('keydown', this.#beiEscape);
  }

  get #ausloeser() {
    return this.querySelectorAll('[aria-controls][aria-expanded]');
  }

  get #offen() {
    return this.querySelector('[aria-expanded="true"]');
  }

  #timer = 0;

  #oeffnen(knopf) {
    clearTimeout(this.#timer);
    for (const k of this.#ausloeser) k.setAttribute('aria-expanded', String(k === knopf));
  }

  #schliessen() {
    clearTimeout(this.#timer);
    for (const k of this.#ausloeser) k.setAttribute('aria-expanded', 'false');
  }

  // Der Ausloeser klappt auf und zu – immer, mit Maus, Finger und Tastatur. Zur
  // Kategorie fuehrt "Alle … ansehen" in der Schiene des Panels; ein Knopf, der mal
  // aufklappt und mal die Seite wechselt, ist fuer Screenreader nicht vorhersehbar.
  #beiKlick = (e) => {
    const knopf = e.target.closest('[aria-controls][aria-expanded]');
    if (!knopf) return;
    if (knopf.getAttribute('aria-expanded') === 'true') this.#schliessen();
    else this.#oeffnen(knopf);
  };

  #beiTaste = (e) => {
    if (e.key !== 'Escape') return;
    const knopf = e.target.closest('[data-er-gruppe]')?.querySelector('[aria-controls]');
    this.#schliessen();
    knopf?.focus();
  };

  #beiEscape = (e) => {
    if (e.key === 'Escape' && this.#offen && !this.contains(document.activeElement)) this.#schliessen();
  };

  #beiFokusAus = (e) => {
    if (!this.contains(e.relatedTarget)) this.#schliessen();
  };

  #beiKlickAussen = (e) => {
    if (this.#offen && !this.contains(e.target)) this.#schliessen();
  };

  #beiZeigerRein = (e) => {
    if (e.pointerType === 'touch') return;
    const knopf = e.currentTarget.querySelector('[aria-controls]');
    // Kurze Verzoegerung: wer mit der Maus nur ueber die Leiste faehrt, oeffnet nichts.
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#oeffnen(knopf), this.#offen ? 0 : 120);
  };

  #beiZeigerRaus = (e) => {
    if (e.pointerType === 'touch') return;
    clearTimeout(this.#timer);
    if (this.contains(document.activeElement) && e.currentTarget.contains(document.activeElement)) return;
    this.#timer = setTimeout(() => this.#schliessen(), 150);
  };
}

/* Schublade unter 990 px: showModal liefert Fokusfalle, Escape und inerten Hintergrund. */
class EratecNavMobil extends HTMLElement {
  connectedCallback() {
    this.knopf = this.querySelector(':scope > [aria-haspopup="dialog"]');
    this.dialog = this.querySelector('dialog');
    this.addEventListener('click', this.#beiKlick);
    /*
       Nicht das close-Ereignis: in eingebetteten WebViews (z. B. der Vorschau im
       Desktop-Programm) bleibt es aus, dann waere der Riegel fuer den Hintergrund
       nie wieder aufgegangen. Das offen-Attribut aendert sich dagegen bei jedem
       Weg, auch bei Escape und beim Klick daneben.
    */
    if (this.dialog) {
      new MutationObserver(() => {
        if (!this.dialog.open) this.#beiSchliessen();
      }).observe(this.dialog, { attributeFilter: ['open'] });
    }
  }

  /*
     Der Hintergrund darf nicht mitscrollen, auch nicht auf dem sichtbaren Streifen
     rechts neben der Schublade. Gescrollt wird in Horizon 4 nicht das Fenster, sondern
     .page-wrapper (height: 100dvh; overflow: auto) – ein Riegel am body hatte deshalb
     keine Wirkung. Der Behaelter wird gesperrt und danach auf seinen Stand
     zurueckgesetzt; touch-action schluckt die Wischgeste auf dem Streifen.
     Fallback fuer Layouts, die das Fenster scrollen: der alte Riegel am body.
  */
  #sperren() {
    // Gescrollt wird je nach Breite .page-wrapper (Horizon setzt dort height/overflow)
    // oder das Dokument selbst. Gesperrt wird das, was wirklich scrollt.
    const huelle = document.querySelector('.page-wrapper');
    this.rolle = huelle && huelle.scrollHeight > huelle.clientHeight ? huelle : null;
    if (this.rolle) {
      this.stand = this.rolle.scrollTop;
      this.rolle.style.overflow = 'hidden';
      this.rolle.style.touchAction = 'none';
      return;
    }
    this.stand = window.scrollY;
    const b = document.body;
    b.style.position = 'fixed';
    b.style.insetInline = '0';
    b.style.width = '100%';
    b.style.top = `${this.stand * -1}px`;
    b.style.touchAction = 'none';
  }

  #freigeben() {
    if (this.stand == null) return;
    if (this.rolle) {
      this.rolle.style.overflow = '';
      this.rolle.style.touchAction = '';
      this.rolle.scrollTop = this.stand;
    } else {
      const b = document.body;
      b.style.position = '';
      b.style.insetInline = '';
      b.style.width = '';
      b.style.top = '';
      b.style.touchAction = '';
      window.scrollTo({ top: this.stand, behavior: 'instant' });
    }
    this.stand = null;
    requestAnimationFrame(leisteNeuFaerben);
  }

  #beiKlick = (e) => {
    if (e.target === this.dialog) {
      // Klick auf den Hintergrund (ausserhalb der Flaeche) schliesst.
      const r = this.dialog.getBoundingClientRect();
      if (e.clientX > r.right || e.clientX < r.left) this.dialog.close();
      return;
    }
    const knopf = e.target.closest('button');
    if (!knopf) return;
    if (knopf === this.knopf) {
      this.#sperren();
      this.dialog.showModal();
      knopf.setAttribute('aria-expanded', 'true');
    } else if (knopf.hasAttribute('data-er-schliessen')) {
      this.dialog.close();
    } else if (knopf.classList.contains('er-schublade__ausloeser') && this.dialog.contains(knopf)) {
      /*
         Nur die Akkordeon-Auslöser der Schublade. Vorher griff dieser Zweig auf JEDEN
         Knopf mit aria-controls in der Schublade zu — also auch auf die Sprachwahl, die
         sich selbst verwaltet (Horizon `dropdown-localization-component`). Beide
         schalteten dann gegeneinander und die Sprachliste ging nie auf (Phase 8).
      */
      const offen = knopf.getAttribute('aria-expanded') === 'true';
      knopf.setAttribute('aria-expanded', String(!offen));
      const bereich = document.getElementById(knopf.getAttribute('aria-controls'));
      if (bereich) bereich.hidden = offen;
    }
  };

  #beiSchliessen = () => {
    this.#freigeben();
    this.knopf?.setAttribute('aria-expanded', 'false');
    this.knopf?.focus();
  };
}

if (!customElements.get('eratec-nav-mega')) customElements.define('eratec-nav-mega', EratecNavMega);
if (!customElements.get('eratec-nav-mobil')) customElements.define('eratec-nav-mobil', EratecNavMobil);

/* --header-height aktuell halten (theme.liquid misst nur einmal vor dem ersten Zeichnen). */
const kopf = document.getElementById('header-component');
if (kopf) {
  new ResizeObserver(([eintrag]) => {
    const hoehe = Math.round(eintrag.borderBoxSize?.[0]?.blockSize ?? kopf.offsetHeight);
    document.body.style.setProperty('--header-height', `${hoehe}px`);
  }).observe(kopf);
}

/*
 * Ueber einem Video-Hero ist der Kopfbereich durchsichtig und wird beim Scrollen
 * deckend. Umgeschaltet wird per IntersectionObserver auf eine Marke, die am oberen
 * Rand des Heros liegt – kein Scroll-Listener, keine Layout-Abfrage je Bild.
 * Das Aussehen der beiden Zustaende steht in sections/eratec-kopfbereich.liquid.
 */
const hero = document.querySelector('.er-hero--ueberlagert');
if (kopf && hero && CSS.supports('selector(:has(*))')) {
  const marke = document.createElement('span');
  marke.className = 'er-kopf-marke';
  marke.setAttribute('aria-hidden', 'true');
  hero.prepend(marke);
  new IntersectionObserver(([eintrag]) => {
    kopf.toggleAttribute('data-fest', !eintrag.isIntersecting);
  }).observe(marke);
}
